
import {DataTableCellEditCompleteEvent} from "primevue/datatable";
import DexDataTable from "@/components/datatable/DexDataTable.vue";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import {defineComponent, nextTick, onMounted, PropType, reactive, ref} from "vue";
import {LocatorKeyword} from "@dex/squeeze-client-ts";
import {helpers} from "@vuelidate/validators";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useRoute} from "vue-router";
import useSqueezeStore from "@/apps/squeeze/store";
import {ClientManager} from "@/singletons/ClientManager";
import {TableListSettings, TableSettings} from "@/util/TableSettings";

export default defineComponent({
	name: "KeyWords",
	components: {
		DexDataTable, Column, InputText,
	},
	props: {
		keywords: {
			type: Array as PropType<LocatorKeyword[]>,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		showErrorMessage: {
			type: Boolean,
		},
	},
	emits: [
		"onClickDelete", "onEntrySelect", "onChangeKeyword",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** The event.data values to check it */
		const entryDataCheck = reactive<LocatorKeyword>({});

		/** Is InputField changed?  */
		const changeInputField = ref<boolean>(false);

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: LocatorKeyword) => {
			emit("onClickDelete", row);
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", {id: 0, locatorId: 0, keyword: ''})
		}

		/**
		 * Triggered when the New Entry Button is clicked
		 * @param row The row to edit
		 */
		const openEditDialog = (row: LocatorKeyword) => {
			emit("onEntrySelect", row)
		}

		/**
		 * Check the keyword is invalid
		 * @param row The row to check if invalid
		 */
		const isInvalidKeyword = (row: LocatorKeyword) => {
			return !helpers.req(row.keyword)
		}

		/**
		 * Focus the editing cell
		 * @param event
		 */
		const onCellEdit = async (event: DataTableCellEditCompleteEvent|any) => {
			await nextTick();
			Object.assign(entryDataCheck, event.data);
			event.originalEvent.target.childNodes[2].focus();
		}

		/**
		 * Check if value is changed, then emit & Triggered on change of any field
		 * (change-Event in editing cell doesn't work in firefox)
		 * @param event
		 */
		const onCellComplete = (event: DataTableCellEditCompleteEvent) => {
			if (changeInputField.value) {
				emit("onChangeKeyword", event, isInvalidKeyword(event.newData));
				changeInputField.value = false;
			} else {
				Object.assign(entryDataCheck, null);

				// We need to trigger this, so the handler move the focus to the next cell is executed
				document.dispatchEvent(new CustomEvent('reloadDone', { detail: event }));
			}
		}

		/**
		 * Check if input was changed
		 * @param inputData
		 */
		const changeEventInInput = (inputData: unknown) => {
			if (JSON.stringify(inputData) !== JSON.stringify(entryDataCheck)) {
				changeInputField.value = true;
			}
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		});

		/** Triggered when page is changed */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		return {
			t,
			toast,
			store,
			tableListPagination,
			entryDataCheck,
			changeInputField,
			openDeleteDialog,
			onNewEntryClick,
			openEditDialog,
			isInvalidKeyword,
			onCellEdit,
			onCellComplete,
			changeEventInInput,
			onChangePage,
		}
	},
});

